import { Box, Grid } from '@mui/material'
import { Form, Formik, FormikValues } from 'formik'
import { useMemo } from 'react'
import { useIntl } from 'react-intl'
import { useCreateUserMutation, useUpdateUserMutation } from '../../../../app/redux-fetch/apiQuery'
import SubmitButtons from '../../../../components/Buttons/SubmitButtons'
import TabWrapper, { TabItem } from '../../../../components/TabWrapper/TabWrapper'
import { useSideModalContext } from '../../../Assets/modals/SideModalContext'
import { useNotificationStack } from '../../../Shipments/hooks/useNotificationStack'
import { IUser } from '../types'
import MainInfo from './MainInfo'
import UserGroupTable from './UserGroupTable'
import { getUserValidationSchema } from './UserValidationSchema'
import messages from './messages'

type UserFormProps = {
  user: IUser
  isDisabled: boolean
}

const UserForm = ({ user, isDisabled }: UserFormProps) => {
  const isNew = !user.id

  const { formatMessage } = useIntl()
  const [createUser] = useCreateUserMutation()
  const [updateUser] = useUpdateUserMutation()

  const validationSchema = getUserValidationSchema(formatMessage)

  const { onClose } = useSideModalContext()

  const { enqueueMsg, enqueue } = useNotificationStack()

  const tabs: TabItem[] = useMemo(() => [
    {
      label: 'Details',
      content: (
        <Grid item xs={12}>
          <MainInfo isDisabled={isDisabled} />
        </Grid>
      ),
    },
    {
      label: 'Groups',
      content: (
        <Grid item xs={12}>
          <UserGroupTable isDisabled={isDisabled} />
        </Grid>
      ),
    },
  ], [isDisabled])

  return (
    <Formik
      initialValues={user}
      onSubmit={async (values: FormikValues) => {
        if (!values.id) {
          await createUser({
            tenantId: values.tenantId ?? -1,
            body: { ...values } as IUser,
          })
          enqueue(formatMessage(messages.userCreated))
        } else {
          await updateUser({
            tenantId: values.tenantId ?? -1,
            body: { ...values } as IUser,
          })

          enqueue(formatMessage(messages.userUpdated))
        }
        onClose()
      }}
      validationSchema={validationSchema}
    >
      <Form>
        <Box sx={{ m: '32px' }}>
          <Box sx={{ width: '100%', p: '40px' }}>
            <TabWrapper tabs={tabs} variant='standard' />
          </Box>
          <Box
            sx={{
              position: 'fixed',
              bottom: 0,
              width: '100%',
              padding: '16px',
              backgroundColor: '#fff',
              borderTop: '1px solid #e0e0e0',
            }}
          >
            {!isDisabled && <SubmitButtons create={isNew} onCancel={onClose} />}
          </Box>
        </Box>
      </Form>
    </Formik>
  )
}

export default UserForm
